import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Tooltip,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Warning,
  Gavel,
  CheckCircle,
  Cancel,
  TrendingUp,
  Message,
  AttachFile,
  Schedule,
  Person,
  Flag,
  Block
} from '@mui/icons-material';
import DialogCloseButton from './common/DialogCloseButton';

/**
 * Admin Dispute Dashboard
 * Professional interface for handling disputes with full resolution workflow
 */
const AdminDisputeDashboard = ({ apiClient }) => {
  const [disputes, setDisputes] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [mediationDialogOpen, setMediationDialogOpen] = useState(false);
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    severity: '',
    teamLevel: '',
    highPriority: false
  });

  // Resolution form state
  const [resolutionForm, setResolutionForm] = useState({
    resolutionType: '',
    resolutionText: '',
    contractorAmount: '',
    clientAmount: '',
    splitPercent: 50,
    notes: ''
  });

  // Escalation form state
  const [escalationReason, setEscalationReason] = useState('');

  // Mediation form state
  const [mediationForm, setMediationForm] = useState({
    scheduledAt: '',
    meetingLink: ''
  });

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  const severityColors = {
    low: 'success',
    medium: 'warning',
    high: 'error',
    critical: 'error'
  };

  const statusColors = {
    open: 'warning',
    investigating: 'info',
    escalated: 'error',
    mediation: 'secondary',
    resolved: 'success',
    closed: 'default'
  };

  useEffect(() => {
    loadDisputes();
    loadStatistics();
  }, [filters]);

  const loadDisputes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/disputes/admin/dashboard', {
        params: filters
      });
      
      if (response.data.success) {
        setDisputes(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await apiClient.get('/disputes/admin/statistics');
      
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleAssignDispute = async (disputeId) => {
    try {
      const response = await apiClient.post(`/disputes/${disputeId}/assign`, {
        autoAssign: true
      });

      if (response.data.success) {
        alert('Dispute assigned successfully');
        loadDisputes();
      }
    } catch (error) {
      alert('Failed to assign dispute: ' + error.message);
    }
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute) return;

    try {
      const payoutDetails = {};
      
      if (resolutionForm.resolutionType === 'partial_release') {
        payoutDetails.contractorAmount = parseFloat(resolutionForm.contractorAmount);
        payoutDetails.clientAmount = parseFloat(resolutionForm.clientAmount);
      } else if (resolutionForm.resolutionType === 'split_payment') {
        payoutDetails.splitPercent = parseFloat(resolutionForm.splitPercent);
      }

      const response = await apiClient.post(`/disputes/${selectedDispute.id}/resolve`, {
        resolutionType: resolutionForm.resolutionType,
        resolutionText: resolutionForm.resolutionText,
        payoutDetails,
        notes: resolutionForm.notes
      });

      if (response.data.success) {
        alert('Dispute resolved successfully');
        setResolveDialogOpen(false);
        setSelectedDispute(null);
        loadDisputes();
      }
    } catch (error) {
      alert('Failed to resolve dispute: ' + error.response?.data?.error || error.message);
    }
  };

  const handleEscalateDispute = async () => {
    if (!selectedDispute || !escalationReason) return;

    try {
      const response = await apiClient.post(`/disputes/${selectedDispute.id}/escalate`, {
        reason: escalationReason
      });

      if (response.data.success) {
        alert('Dispute escalated successfully');
        setEscalateDialogOpen(false);
        setEscalationReason('');
        setSelectedDispute(null);
        loadDisputes();
      }
    } catch (error) {
      alert('Failed to escalate dispute: ' + error.message);
    }
  };

  const handleScheduleMediation = async () => {
    if (!selectedDispute) return;

    try {
      const response = await apiClient.post(`/disputes/${selectedDispute.id}/mediation`, mediationForm);

      if (response.data.success) {
        alert('Mediation session scheduled');
        setMediationDialogOpen(false);
        setMediationForm({ scheduledAt: '', meetingLink: '' });
        setSelectedDispute(null);
        loadDisputes();
      }
    } catch (error) {
      alert('Failed to schedule mediation: ' + error.message);
    }
  };

  const loadMessages = async (disputeId) => {
    try {
      const response = await apiClient.get(`/disputes/${disputeId}/messages`);
      
      if (response.data.success) {
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!selectedDispute || !newMessage.trim()) return;

    try {
      await apiClient.post(`/disputes/${selectedDispute.id}/messages`, {
        message: newMessage,
        messageType: 'message',
        isInternal: false
      });

      setNewMessage('');
      loadMessages(selectedDispute.id);
    } catch (error) {
      alert('Failed to send message: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const getFraudIndicator = (score) => {
    if (score >= 70) return { label: 'HIGH RISK', color: 'error' };
    if (score >= 50) return { label: 'MEDIUM RISK', color: 'warning' };
    if (score >= 30) return { label: 'LOW RISK', color: 'info' };
    return { label: 'NORMAL', color: 'success' };
  };

  const renderStatistics = () => {
    if (!statistics) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Disputes
              </Typography>
              <Typography variant="h4">
                {statistics.total_disputes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Open Disputes
              </Typography>
              <Typography variant="h4" color="warning.main">
                {statistics.open_disputes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Resolution Time
              </Typography>
              <Typography variant="h4">
                {statistics.avg_resolution_hours ? `${Math.round(statistics.avg_resolution_hours)}h` : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                High Fraud Cases
              </Typography>
              <Typography variant="h4" color="error.main">
                {statistics.high_fraud_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderFilters = () => (
    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="investigating">Investigating</MenuItem>
              <MenuItem value="escalated">Escalated</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Severity</InputLabel>
            <Select
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              label="Severity"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Team Level</InputLabel>
            <Select
              value={filters.teamLevel}
              onChange={(e) => setFilters({ ...filters, teamLevel: e.target.value })}
              label="Team Level"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="LEVEL_1">Level 1</MenuItem>
              <MenuItem value="LEVEL_2">Level 2</MenuItem>
              <MenuItem value="LEVEL_3">Level 3</MenuItem>
              <MenuItem value="LEGAL">Legal</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            variant={filters.highPriority ? 'contained' : 'outlined'}
            fullWidth
            startIcon={<Warning />}
            onClick={() => setFilters({ ...filters, highPriority: !filters.highPriority })}
          >
            High Priority Only
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderDisputesTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Raised By</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Severity</TableCell>
            <TableCell>Fraud Score</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {disputes.map((dispute) => {
            const fraudIndicator = getFraudIndicator(dispute.fraud_score);
            
            return (
              <TableRow key={dispute.id}>
                <TableCell>{dispute.id.substring(0, 8)}</TableCell>
                <TableCell>{dispute.project_id}</TableCell>
                <TableCell>
                  {dispute.raised_by_name}
                  <br />
                  <Typography variant="caption">{dispute.raised_by_email}</Typography>
                </TableCell>
                <TableCell>{formatCurrency(dispute.total_amount)}</TableCell>
                <TableCell>
                  <Chip label={dispute.dispute_reason.replace('_', ' ')} size="small" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={dispute.severity_level || 'N/A'} 
                    color={severityColors[dispute.severity_level] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${dispute.fraud_score || 0}% - ${fraudIndicator.label}`}
                    color={fraudIndicator.color}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={dispute.status}
                    color={statusColors[dispute.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(dispute.created_at)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!dispute.assigned_to && (
                      <Tooltip title="Assign">
                        <IconButton 
                          size="small"
                          onClick={() => handleAssignDispute(dispute.id)}
                        >
                          <Person />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Resolve">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          setSelectedDispute(dispute);
                          setResolveDialogOpen(true);
                        }}
                      >
                        <Gavel />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Escalate">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setSelectedDispute(dispute);
                          setEscalateDialogOpen(true);
                        }}
                      >
                        <TrendingUp />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Messages">
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setSelectedDispute(dispute);
                          loadMessages(dispute.id);
                          setMessagesDialogOpen(true);
                        }}
                      >
                        <Message />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Schedule Mediation">
                      <IconButton 
                        size="small"
                        onClick={() => {
                          setSelectedDispute(dispute);
                          setMediationDialogOpen(true);
                        }}
                      >
                        <Schedule />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <Gavel sx={{ mr: 1, verticalAlign: 'middle' }} />
        Dispute Resolution Dashboard
      </Typography>

      {renderStatistics()}
      {renderFilters()}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        renderDisputesTable()
      )}

      {/* Resolve Dispute Dialog */}
      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setResolveDialogOpen(false)} />
        <DialogTitle>Resolve Dispute</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Resolution Type</InputLabel>
              <Select
                value={resolutionForm.resolutionType}
                onChange={(e) => setResolutionForm({ ...resolutionForm, resolutionType: e.target.value })}
                label="Resolution Type"
              >
                <MenuItem value="release_to_contractor">Release to Contractor</MenuItem>
                <MenuItem value="refund_to_client">Refund to Client</MenuItem>
                <MenuItem value="partial_release">Partial Release</MenuItem>
                <MenuItem value="split_payment">Split Payment</MenuItem>
                <MenuItem value="mediation_required">Mediation Required</MenuItem>
                <MenuItem value="no_action">No Action</MenuItem>
              </Select>
            </FormControl>

            {resolutionForm.resolutionType === 'partial_release' && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Contractor Amount"
                    type="number"
                    value={resolutionForm.contractorAmount}
                    onChange={(e) => setResolutionForm({ ...resolutionForm, contractorAmount: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Client Refund Amount"
                    type="number"
                    value={resolutionForm.clientAmount}
                    onChange={(e) => setResolutionForm({ ...resolutionForm, clientAmount: e.target.value })}
                  />
                </Grid>
              </Grid>
            )}

            {resolutionForm.resolutionType === 'split_payment' && (
              <TextField
                fullWidth
                label="Contractor Split Percentage"
                type="number"
                value={resolutionForm.splitPercent}
                onChange={(e) => setResolutionForm({ ...resolutionForm, splitPercent: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Percentage going to contractor (client gets the remainder)"
              />
            )}

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Resolution Explanation"
              value={resolutionForm.resolutionText}
              onChange={(e) => setResolutionForm({ ...resolutionForm, resolutionText: e.target.value })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Internal Notes"
              value={resolutionForm.notes}
              onChange={(e) => setResolutionForm({ ...resolutionForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResolveDispute}>
            Resolve Dispute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={escalateDialogOpen} onClose={() => setEscalateDialogOpen(false)}>
        <DialogCloseButton onClick={() => setEscalateDialogOpen(false)} />
        <DialogTitle>Escalate Dispute</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Escalation Reason"
            value={escalationReason}
            onChange={(e) => setEscalationReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscalateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleEscalateDispute}>
            Escalate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mediation Dialog */}
      <Dialog open={mediationDialogOpen} onClose={() => setMediationDialogOpen(false)}>
        <DialogCloseButton onClick={() => setMediationDialogOpen(false)} />
        <DialogTitle>Schedule Mediation Session</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Scheduled Time"
              value={mediationForm.scheduledAt}
              onChange={(e) => setMediationForm({ ...mediationForm, scheduledAt: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Meeting Link (Zoom, Teams, etc.)"
              value={mediationForm.meetingLink}
              onChange={(e) => setMediationForm({ ...mediationForm, meetingLink: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMediationDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleScheduleMediation}>
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={messagesDialogOpen} onClose={() => setMessagesDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={() => setMessagesDialogOpen(false)} />
        <DialogTitle>Dispute Messages</DialogTitle>
        <DialogContent>
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {messages.map((msg, index) => (
              <React.Fragment key={msg.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={msg.sender_name}
                    secondary={
                      <>
                        <Typography variant="body2">{msg.message}</Typography>
                        <Typography variant="caption">{formatDate(msg.created_at)}</Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < messages.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="New Message"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessagesDialogOpen(false)}>Close</Button>
          <Button variant="contained" onClick={sendMessage}>
            Send Message
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDisputeDashboard;

